import React, { useState, useMemo } from 'react';
import { Card, Table, Button } from 'react-bootstrap';
import { FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';
import { COLUNAS_DEBITOS, PAGE_SIZE } from './constants';

export default function TabelaDebitos({ dados }) {
  const [pagina, setPagina] = useState(1);
  const [sortKey, setSortKey] = useState('data');
  const [sortDir, setSortDir] = useState('asc');

  const itensComSaldo = useMemo(() => {
    if (!dados || !dados.itens) return [];

    // Ordenar por data ASC antes de calcular saldo corrente (mesmo comportamento do V1)
    const itens = dados.itens.slice().sort((a, b) => {
      const dateA = a.data ? new Date(a.data) : new Date(0);
      const dateB = b.data ? new Date(b.data) : new Date(0);
      return dateA - dateB;
    });

    let saldo = dados.saldo || 0;

    for (let i = itens.length - 1; i >= 0; i--) {
      saldo += parseFloat(itens[i].valor) || 0;
    }

    const resultado = [];
    for (let j = 0; j < itens.length; j++) {
      const valor = parseFloat(itens[j].valor) || 0;
      const saldoLinha = valor - saldo;
      saldo -= valor;
      resultado.push({
        data: itens[j].data,
        tipo: itens[j].tipo,
        descricao: itens[j].descricao,
        valor: valor,
        saldo: saldoLinha,
        saldoAtualizado: saldo,
      });
    }
    return resultado;
  }, [dados]);

  const dadosOrdenados = useMemo(() => {
    if (!itensComSaldo || itensComSaldo.length === 0) return [];
    return [].concat(itensComSaldo).sort((a, b) => {
      const valA = a[sortKey] != null ? a[sortKey] : '';
      const valB = b[sortKey] != null ? b[sortKey] : '';
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [itensComSaldo, sortKey, sortDir]);

  const totalPaginas = Math.ceil(dadosOrdenados.length / PAGE_SIZE);
  const dadosPaginados = dadosOrdenados.slice(
    (pagina - 1) * PAGE_SIZE,
    pagina * PAGE_SIZE
  );

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPagina(1);
  };

  const renderSortIcon = (key) => {
    if (sortKey !== key) return <FaSort size={10} className="ml-1 text-muted" />;
    return sortDir === 'asc'
      ? <FaSortUp size={10} className="ml-1" />
      : <FaSortDown size={10} className="ml-1" />;
  };

  const formatDate = (val) => {
    if (!val) return '';
    return new Date(val).toLocaleDateString('pt-BR');
  };

  const formatMoney = (val) => {
    if (val == null) return '';
    return parseFloat(val).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <Card className="mb-2 shadow-sm" style={{ borderTop: '3px solid #0747a6' }}>
      <Card.Body className="py-2 px-3">
        <div className="table-responsive">
          <Table hover size="sm" bordered>
            <thead style={{ backgroundColor: '#0747a6', color: '#fff' }}>
              <tr>
                {COLUNAS_DEBITOS.map(col => (
                  <th
                    key={col.key}
                    style={{
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      textAlign: col.align || 'center',
                    }}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label} {renderSortIcon(col.key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dadosPaginados.map((item, idx) => {
                const corValor = item.valor < 0 ? 'red' : 'inherit';
                const corSaldo = item.saldoAtualizado > 0 ? 'red' : 'black';

                return (
                  <tr key={idx}>
                    <td style={{ color: corValor }}>{formatDate(item.data)}</td>
                    <td
                      style={{ color: corValor }}
                      dangerouslySetInnerHTML={{ __html: item.descricao }}
                    />
                    <td className="text-right" style={{ color: corValor }}>
                      {formatMoney(item.valor)}
                    </td>
                    <td className="text-right" style={{ color: corSaldo }}>
                      {formatMoney(item.saldo)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>

        {dadosPaginados.length === 0 && (
          <div className="text-center py-2">
            <span className="badge badge-info">
              Não houve resultados para os filtros informados.
            </span>
          </div>
        )}

        {totalPaginas > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-2">
            <small className="text-muted">
              Mostrando {(pagina - 1) * PAGE_SIZE + 1} -{' '}
              {Math.min(pagina * PAGE_SIZE, dadosOrdenados.length)} de{' '}
              {dadosOrdenados.length} registros
            </small>
            <div>
              <Button
                variant="outline-primary"
                size="sm"
                disabled={pagina <= 1}
                onClick={() => setPagina(pagina - 1)}
              >
                Anterior
              </Button>{' '}
              <span className="mx-2">{pagina} / {totalPaginas}</span>
              <Button
                variant="outline-primary"
                size="sm"
                disabled={pagina >= totalPaginas}
                onClick={() => setPagina(pagina + 1)}
              >
                Proximo
              </Button>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
